import { z } from 'zod';

/*
 * Invoice form schema (spec §7.2). Amount is entered in major units (dollars)
 * for the user, then converted to integer minor units before hitting the API —
 * the FE formats/parses money but never does arithmetic on stored values.
 */
export const invoiceFormSchema = z.object({
  clientId: z.string().min(1, 'Client is required.'),
  amountMajor: z
    .number({ message: 'Amount is required.' })
    .min(0, 'Amount must be non-negative.'),
  currency: z.string().length(3, 'Use a 3-letter ISO currency code.'),
  dueAt: z.string().min(1, 'Due date is required.'),
});

export type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;

/** Convert form values to the API input shape (major → minor units). */
export function toInvoiceInput(values: InvoiceFormValues) {
  return {
    clientId: values.clientId,
    amountMinor: Math.round(values.amountMajor * 100),
    currency: values.currency.toUpperCase(),
    // `dueAt` is a calendar day (`YYYY-MM-DD` from a date input) but the wire
    // contract types it as `date-time` (schema.d.ts). Encode it as midnight
    // UTC so it round-trips to the picked day when rendered in UTC (DateCell
    // variant="calendar-day"). Anchoring explicitly to `T00:00:00Z` keeps the
    // intent clear and avoids depending on how a bare date string is parsed.
    dueAt: calendarDayToUtcInstant(values.dueAt),
  };
}

/*
 * Encode a `YYYY-MM-DD` calendar day as a midnight-UTC ISO instant. A
 * well-formed date maps to `${iso}T00:00:00.000Z`; anything else falls back to
 * `new Date(...)` so an unexpected shape still produces a valid ISO string
 * rather than throwing (Zod already guarantees a non-empty value upstream).
 */
function calendarDayToUtcInstant(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? `${value}T00:00:00.000Z`
    : new Date(value).toISOString();
}
